import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
} from '@mui/material';

import {
  ButtonsContainer,
  ButtonsWrapper,
  CloseButtonWrapper,
  ConfirmModalWrapper,
  ConsentError,
  ConsentsSection,
  ContentWrapper,
  DialogWrapper,
  HeaderContentWrapper,
  HintWrapper,
  InvekosHeader,
  InvekosInputContainer,
  InvekosWrapper,
  LastSavedInfo,
  ModalContent,
  ModalDescription,
  ModalHeader,
  QuestionDescription,
  QuestionsContainer,
  QuestionSection,
  QuestionText,
  RadioGroupWrapper,
  StyledDialogTitle,
  WarningMessage,
} from './styles';
import CustomRadioGroup from '../../../components/Inputs/RadioButtonGroup/RadioButtonGroup';
import Hint from '../../../components/Hint/Hint';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { customPalette } from '../../../../styles/theme';
import { SurveyQuestionDto } from '../../../../services/cropConnectApiClient';
import {
  AnswerTypeEnum,
  QuestionTypeEnum,
} from '../../survey_red_cert/SurveyQuestion/types';
import InvekosNumberInput from '../../../components/Inputs/InvekosNumberInput/InvekosNumberInput';
import { useSurveyRedCertStore } from '../../../../stores/storeRoot';
import CloseIcon from '../../../../assets/icons/modalsIcons/CloseIcon';
import { ConfirmActionModal } from '../../survey_red_cert/ConfirmActionModal/ConfirmActionModal';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { SurveyFormModalProps } from './types';

const SurveyFormModal: React.FC<SurveyFormModalProps> = ({
  open,
  onClose,
  surveyName,
  questions,
  invekosNumber,
  filledBy,
  lastSubmissionTime,
  surveyId,
  additionalProperties,
  previousAnswers,
  isFilledOut,
  onSubmitSuccess,
}) => {
  const { submitSurveyAnswers } = useSurveyRedCertStore();
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [invalidQuestions, setInvalidQuestions] = useState<string[]>([]);
  const [, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [userInvekosNumber, setUserInvekosNumber] = useState('');
  const [isInvekosNumberMissing, setIsInvekosNumberMissing] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(!!isFilledOut);
  const [hasChanges, setHasChanges] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);

  const { t } = useI18n();

  const lastSubmissionDateTime = lastSubmissionTime
    ? new Date(lastSubmissionTime)
    : new Date();
  useEffect(() => {
    const initialAnswers = questions.reduce(
      (acc: { [key: string]: string }, question) => {
        const existingAnswer = previousAnswers?.find(
          (answer) => answer.key === question.accessId
        );

        if (existingAnswer) {
          acc[question.accessId] = existingAnswer.value;
        } else if (question.properties.type === QuestionTypeEnum.PREFILLED) {
          acc[question.accessId] = question.properties.prefilledAnswer || '';
        }

        return acc;
      },
      {}
    );

    setAnswers(initialAnswers);
  }, [questions, previousAnswers]);

  useEffect(() => {
    setIsConsentChecked(!!isFilledOut);
  }, [isFilledOut]);

  const validateForm = () => {
    const invalidIds = questions
      .filter((question) => {
        if (question.properties?.type === QuestionTypeEnum.PREFILLED)
          return false;

        const answer = answers[question.accessId!];
        if (answer === undefined) {
          return true;
        }

        return !answer?.trim();
      })
      .map((q) => q.accessId!);

    setInvalidQuestions(invalidIds);
    setConsentError(!isConsentChecked);

    if (!invekosNumber && !userInvekosNumber) {
      setIsInvekosNumberMissing(true);
      return false;
    } else {
      setIsInvekosNumberMissing(false);
    }

    return invalidIds.length === 0 && isConsentChecked;
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setInvalidQuestions((prev) => prev.filter((id) => id !== questionId));
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const answersPayload = Object.entries(answers).reduce(
        (acc, [questionId, answer]) => {
          acc[questionId] = answer;
          return acc;
        },
        {} as { [key: string]: string }
      );

      await submitSurveyAnswers(surveyId, {
        answers: answersPayload,
        additionalData: {
          invekosno: invekosNumber || userInvekosNumber,
          lastSubmissionTime: new Date().toISOString(),
        },
      });

      setHasChanges(false);

      onSubmitSuccess?.();

      setTimeout(() => {
        closeModal();
      }, 500);
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setIsConfirmCloseModalOpen(true);
    } else {
      closeModal();
    }
  };

  const closeModal = () => {
    onClose();
    setAnswers({});
    setInvalidQuestions([]);
    setHasAttemptedSubmit(false);
    if (!isFilledOut) {
      setIsConsentChecked(false);
    }
    setConsentError(false);
    setUserInvekosNumber('');
    setHasChanges(false);
  };

  const formatDateTime = (date: Date) => {
    const formattedDate = date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const formattedTime = date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${formattedDate} ${formattedTime}`;
  };

  const renderQuestionInput = (question: SurveyQuestionDto) => {
    // @ts-expect-error to-do: add types
    const { type, answerType, possibleAnswers = [] } = question.properties;

    if (type === QuestionTypeEnum.PREFILLED) {
      return null;
    }

    switch (answerType) {
      case AnswerTypeEnum.YES_NO:
        return (
          <RadioGroupWrapper>
            <CustomRadioGroup
              name={question.accessId!}
              options={possibleAnswers.map((answer: string, index: number) => ({
                //YES_NO answers sends (per convention) 'true' for the first answer and 'false' for the second answer
                value: index === 0,
                label: answer.charAt(0).toUpperCase() + answer.slice(1),
              }))}
              value={answers[question.accessId!] || ''}
              onChange={(value) =>
                handleAnswerChange(question.accessId!, value)
              }
            />
          </RadioGroupWrapper>
        );

      case AnswerTypeEnum.TEXTAREA:
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={answers[question.accessId!] || ''}
            onChange={(e) =>
              handleAnswerChange(question.accessId!, e.target.value)
            }
            variant="outlined"
            error={invalidQuestions.includes(question.accessId!)}
            helperText={
              invalidQuestions.includes(question.accessId!) &&
              t(TranslationKeys.survey_form_modal_required_field)
            }
          />
        );

      default:
        return (
          <TextField
            fullWidth
            value={answers[question.accessId!] || ''}
            onChange={(e) =>
              handleAnswerChange(question.accessId!, e.target.value)
            }
            variant="outlined"
            error={invalidQuestions.includes(question.accessId!)}
            helperText={
              invalidQuestions.includes(question.accessId!) &&
              t(TranslationKeys.survey_form_modal_required_field)
            }
          />
        );
    }
  };

  return (
    <>
      <DialogWrapper open={open} onClick={(e) => e.preventDefault()}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <ModalContent>
            <ModalHeader>
              <HeaderContentWrapper>
                <StyledDialogTitle>{surveyName}</StyledDialogTitle>
                <CloseButtonWrapper>
                  <IconButton
                    className="modal-close-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClose();
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </CloseButtonWrapper>
              </HeaderContentWrapper>
            </ModalHeader>

            <QuestionsContainer>
              <ContentWrapper>
                {additionalProperties?.description && (
                  <ModalDescription variant="body1">
                    {additionalProperties.description}
                  </ModalDescription>
                )}

                {additionalProperties?.hint?.enabled &&
                  additionalProperties?.hint?.text && (
                    <HintWrapper>
                      <Hint
                        text={additionalProperties.hint.text}
                        readonlyHint
                      />
                    </HintWrapper>
                  )}

                <InvekosWrapper>
                  <InvekosInputContainer>
                    <InvekosHeader isMissing={isInvekosNumberMissing}>
                      {t(TranslationKeys.survey_form_modal_invekos_header)}
                    </InvekosHeader>
                    {invekosNumber ? (
                      <InvekosNumberInput
                        value={invekosNumber}
                        readonly={true}
                      />
                    ) : (
                      <InvekosNumberInput
                        onValidNumber={(number) => {
                          setUserInvekosNumber(number);
                          setHasChanges(true);
                        }}
                        onInvalidNumber={() => {
                          setUserInvekosNumber('');
                        }}
                      />
                    )}
                    {additionalProperties?.hintForInvekos && (
                      <Hint
                        text={additionalProperties.hintForInvekos}
                        readonlyHint
                      />
                    )}
                  </InvekosInputContainer>

                  {filledBy && (
                    <LastSavedInfo>
                      {t(TranslationKeys.survey_form_modal_last_saved_by)}{' '}
                      <b>
                        <i>{filledBy}</i>
                      </b>{' '}
                      {t(TranslationKeys.survey_form_modal_last_saved_on)}{' '}
                      <b>
                        {formatDateTime(lastSubmissionDateTime).replace(
                          ' ',
                          ', '
                        )}
                      </b>
                    </LastSavedInfo>
                  )}
                </InvekosWrapper>

                {questions
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((question) => (
                    <QuestionSection
                      key={question.accessId}
                      hasError={invalidQuestions.includes(question.accessId!)}
                    >
                      <QuestionText
                        hasError={invalidQuestions.includes(question.accessId!)}
                      >
                        {`${question.properties?.text}`}
                      </QuestionText>

                      {question.properties?.description && (
                        <QuestionDescription variant="body2">
                          {question.properties.description}
                        </QuestionDescription>
                      )}

                      {renderQuestionInput(question)}

                      {question.properties?.hint?.enabled && (
                        <Hint
                          text={question.properties.hint.text}
                          readonlyHint
                          disabled={false}
                        />
                      )}
                    </QuestionSection>
                  ))}

                <ConsentsSection>
                  <p style={{ whiteSpace: 'pre-wrap' }}>
                    {additionalProperties?.consents?.extraConsent || ' '}
                  </p>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isConsentChecked}
                        onChange={(e) => {
                          setIsConsentChecked(e.target.checked);
                          setConsentError(false);
                        }}
                        color="primary"
                        disabled={!!isFilledOut}
                      />
                    }
                    label={additionalProperties?.consents?.mainConsent || ' '}
                    sx={{
                      width: '100%',
                      '& .MuiTypography-root': {
                        color: consentError
                          ? customPalette.negative.main
                          : customPalette.grey.main,
                        fontSize: '16px',
                      },
                      whiteSpace: 'pre-wrap',
                    }}
                  />
                  {consentError && (
                    <ConsentError>
                      {t(TranslationKeys.survey_form_modal_consent_error)}
                    </ConsentError>
                  )}
                </ConsentsSection>
              </ContentWrapper>

              <ButtonsContainer>
                <ButtonsWrapper>
                  <ActionButton
                    variant="ghost"
                    buttonText={t(
                      TranslationKeys.survey_form_modal_cancel_button
                    )}
                    onClickAction={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClose();
                    }}
                    disabled={isSubmitting}
                  />
                  {(invalidQuestions.length > 0 ||
                    consentError ||
                    isInvekosNumberMissing) && (
                    <WarningMessage>
                      {t(
                        TranslationKeys.survey_form_modal_incomplete_answers_warning
                      )}
                    </WarningMessage>
                  )}
                  <ActionButton
                    variant="accept-blue"
                    buttonText={
                      isSubmitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        t(TranslationKeys.survey_form_modal_submit_button)
                      )
                    }
                    onClickAction={handleSubmit}
                    disabled={isSubmitting}
                  />
                </ButtonsWrapper>
              </ButtonsContainer>
            </QuestionsContainer>
          </ModalContent>
        </div>
      </DialogWrapper>

      {isConfirmCloseModalOpen && (
        <ConfirmModalWrapper>
          <ConfirmActionModal
            isOpen={isConfirmCloseModalOpen}
            onClose={() => setIsConfirmCloseModalOpen(false)}
            onConfirm={closeModal}
            title={
              t(TranslationKeys.survey_form_modal_close_modal_title) as string
            }
            content={
              t(TranslationKeys.survey_form_modal_close_modal_content) as string
            }
            confirmButtonText={
              t(
                TranslationKeys.survey_form_modal_confirm_cancel_button
              ) as string
            }
          />
        </ConfirmModalWrapper>
      )}
    </>
  );
};

export default SurveyFormModal;
